import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type MarketRegime = 'favorable' | 'cautious' | 'adverse';

interface RegimeBadgeProps {
  regime: MarketRegime | null;
  probability: number;
  tradingDate: string | null;
  observationCount: number;
  insufficient: boolean;
  isLoading: boolean;
  regimeSince?: string | null;
  previousRegime?: MarketRegime | null;
}

const REGIME_CONFIG: Record<MarketRegime, {
  label: string;
  bgClass: string;
  textClass: string;
  dotColor: string;
  glowColor: string;
}> = {
  favorable: {
    label: 'Favorable',
    bgClass: 'bg-green-500/15 dark:bg-green-500/20',
    textClass: 'text-green-600 dark:text-green-400',
    dotColor: '#22C55E',
    glowColor: 'rgba(34, 197, 94, 0.4)',
  },
  cautious: {
    label: 'Cautious',
    bgClass: 'bg-amber-400/15 dark:bg-amber-400/20',
    textClass: 'text-amber-600 dark:text-amber-400',
    dotColor: '#FBBF24',
    glowColor: 'rgba(251, 191, 36, 0.35)',
  },
  adverse: {
    label: 'Adverse',
    bgClass: 'bg-red-600/15 dark:bg-red-600/20',
    textClass: 'text-red-600 dark:text-red-400',
    dotColor: '#DC2626',
    glowColor: 'rgba(220, 38, 38, 0.4)',
  },
};

export function RegimeBadge({
  regime,
  probability,
  tradingDate,
  observationCount,
  insufficient,
  isLoading,
  regimeSince,
  previousRegime,
}: RegimeBadgeProps) {
  const [showInfo, setShowInfo] = useState(false);

  if (isLoading) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-200/50 dark:bg-gray-700/50 animate-pulse">
        <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500" />
        <span className="text-xs font-medium text-gray-400 dark:text-gray-500">Loading...</span>
      </div>
    );
  }

  if (insufficient || !regime) {
    return (
      <div className="inline-flex items-center gap-2">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-200/50 dark:bg-gray-700/50"
          title={`Need ${30 - observationCount} more trading days for regime detection`}
        >
          <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500" />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Regime: Insufficient data
          </span>
        </div>
      </div>
    );
  }

  const config = REGIME_CONFIG[regime];
  const confidencePct = Math.round(probability * 100);

  return (
    <div className="inline-flex items-center gap-2 relative">
      <motion.div
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${config.bgClass} cursor-default`}
        style={{
          boxShadow: `0 0 14px ${config.glowColor}, 0 0 5px ${config.glowColor}`,
        }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        title={`Regime: ${config.label} (${confidencePct}% confidence)\nBased on ${observationCount} trading days\nLast updated: ${tradingDate}`}
      >
        <motion.div
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: config.dotColor }}
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className={`text-sm font-bold ${config.textClass}`}>
          {config.label}
        </span>
        {regimeSince && previousRegime && (
          <span className={`text-[11px] font-medium ml-1 ${config.textClass} opacity-70`}>
            since {new Date(regimeSince + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            <span className="text-gray-400 dark:text-gray-500 mx-0.5">|</span>
            <span className="text-gray-400 dark:text-gray-500">was </span>
            <span className={REGIME_CONFIG[previousRegime].textClass}>{REGIME_CONFIG[previousRegime].label}</span>
          </span>
        )}
      </motion.div>

      {/* Info button */}
      <button
        onClick={() => setShowInfo(!showInfo)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200/60 dark:bg-gray-700/60 hover:bg-gray-300/80 dark:hover:bg-gray-600/80 transition-colors cursor-pointer"
        title="What is market regime?"
      >
        <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">?</span>
      </button>

      {/* Info popover */}
      <AnimatePresence>
        {showInfo && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 z-50 w-80 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-4"
          >
            <div className="flex justify-between items-start mb-3">
              <h4 className="text-sm font-bold text-gray-900 dark:text-white">Market Regime Detection</h4>
              <button
                onClick={() => setShowInfo(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xs cursor-pointer"
              >
                X
              </button>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
              A Hidden Markov Model analyzes daily market features (returns, volatility, range, dispersion) across your basket tickers to detect which regime the market is in. Independent of trading activity.
            </p>

            <div className="space-y-2.5">
              <div className="flex items-start gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs font-semibold text-green-600 dark:text-green-400">Favorable</span>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Strategy is consistently profitable. Trade normal or increase size.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-400 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Cautious</span>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Mixed results, no clear edge. Trade normal size, tighten stops.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-3 h-3 rounded-full bg-red-600 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs font-semibold text-red-600 dark:text-red-400">Adverse</span>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Strategy is bleeding. Reduce size or pause trading until regime shifts.</p>
                </div>
              </div>
            </div>

            <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-700">
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Model: 3-state multivariate Gaussian HMM | 4D market features | 5-day smoothing | Updated daily
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
